import { SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG } from "../components/weather/radar/siataSantaElenaRadarOverlay.js";

const BOGOTA_TIMEZONE = "America/Bogota";

export const SIATA_SANTA_ELENA_PLAYBACK_INTERVAL_MS = 800;
export const SIATA_SANTA_ELENA_STALE_FRAME_MINUTES = 15;

export function formatSiataSantaElenaFrameDisplayTime(timestampUtc) {
  if (!timestampUtc) {
    return "—";
  }

  const date = new Date(timestampUtc);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleTimeString(undefined, {
    timeZone: BOGOTA_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function normalizeSiataSantaElenaCachedFrame(raw) {
  if (!raw?.valid || !raw?.renderable || !raw?.publicUrl) {
    return null;
  }

  const timestampUtc = raw.timestampUtc || raw.discoveredAt || null;

  return {
    id: `siata-santa-elena-${timestampUtc || raw.cacheFilename || raw.filename}`,
    source: raw.source || SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG.sourceKey,
    sourceLabel: raw.sourceLabel || SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG.sourceLabel,
    trustLabel: raw.trustLabel || SIATA_SANTA_ELENA_RADAR_OVERLAY_CONFIG.trustLabel,
    timestampUtc,
    displayTime: formatSiataSantaElenaFrameDisplayTime(timestampUtc),
    ageMinutes: Number.isFinite(Number(raw.ageMinutes)) ? Number(raw.ageMinutes) : null,
    imageUrl: raw.publicUrl,
    publicUrl: raw.publicUrl,
    filename: raw.cacheFilename || raw.filename || null,
    renderable: true,
    georefFrame: raw.georefFrame || null,
  };
}

export function normalizeSiataSantaElenaCachedFrames(apiPayload) {
  const rawFrames = Array.isArray(apiPayload?.frames) ? apiPayload.frames : [];

  return rawFrames
    .map(normalizeSiataSantaElenaCachedFrame)
    .filter(Boolean)
    .sort((left, right) => {
      const leftTime = left.timestampUtc ? new Date(left.timestampUtc).getTime() : 0;
      const rightTime = right.timestampUtc ? new Date(right.timestampUtc).getTime() : 0;
      return leftTime - rightTime;
    });
}

export function findSiataSantaElenaFrameIndexByTimestamp(frames = [], timestampUtc) {
  if (!timestampUtc) {
    return -1;
  }

  return frames.findIndex((frame) => frame.timestampUtc === timestampUtc);
}

export function resolveSiataSantaElenaFrameGaps(frames = []) {
  if (frames.length < 2) {
    return [];
  }

  const gaps = [];

  for (let index = 1; index < frames.length; index += 1) {
    const previous = new Date(frames[index - 1].timestampUtc).getTime();
    const current = new Date(frames[index].timestampUtc).getTime();
    const gapMinutes = (current - previous) / (60 * 1000);

    if (gapMinutes > 10) {
      gaps.push({
        afterIndex: index - 1,
        gapMinutes: Math.round(gapMinutes),
      });
    }
  }

  return gaps;
}

export function resolveSiataSantaElenaAnimationStatus({
  enabled = false,
  loading = false,
  error = "",
  frameCount = 0,
  latestFrame = null,
}) {
  if (!enabled) {
    return "disabled";
  }

  if (loading) {
    return "loading";
  }

  if (error) {
    return "error";
  }

  if (frameCount < 1) {
    return "empty";
  }

  if (frameCount === 1) {
    return "latest_only";
  }

  return "ready";
}

export function isSiataSantaElenaFrameStale(frame, thresholdMinutes = SIATA_SANTA_ELENA_STALE_FRAME_MINUTES) {
  if (!frame) {
    return false;
  }

  const ageMinutes = Number(frame.ageMinutes);
  if (Number.isFinite(ageMinutes)) {
    return ageMinutes > thresholdMinutes;
  }

  if (!frame.timestampUtc) {
    return false;
  }

  const ageMs = Date.now() - new Date(frame.timestampUtc).getTime();
  return ageMs > thresholdMinutes * 60 * 1000;
}
